import { forwardRef, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from "web-design-system";
import { ChevronDownIcon } from "lucide-react";
import {
  type CallingCodeOption,
  getCallingCodeOptions,
  getFlagImageUrl,
} from "creso-core/utils";

const INDIA_CALLING_CODE = "+91";

function findSelectedOption(
  options: CallingCodeOption[],
  value: string,
  iso: string
) {
  return (
    options.find((o) => o.iso === iso && value.startsWith(o.callingCode)) ??
    // Longest matching code wins, e.g. +1268 over +1
    options
      .filter((o) => value.startsWith(o.callingCode))
      .sort((a, b) => b.callingCode.length - a.callingCode.length)[0] ??
    options.find((o) => o.callingCode === INDIA_CALLING_CODE)!
  );
}

function Flag({ iso }: { iso: string }) {
  return (
    <img
      src={getFlagImageUrl(iso)}
      alt=""
      className="h-3.5 w-5 shrink-0 rounded-xs object-cover"
    />
  );
}

type CountryCodePhoneInputProps = {
  label?: React.ReactNode;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  /** When false, the code is fixed to +91 */
  allowCountryChange?: boolean;
  /** Classes for the bordered input row, e.g. to change its height */
  className?: string;
};

/** Mobile input with a searchable country code picker; emits E.164 (e.g. +919999999999). */
export const FormCountryCodePhoneInput = forwardRef<
  HTMLInputElement,
  CountryCodePhoneInputProps
>(
  (
    {
      label,
      value = "",
      onChange,
      onBlur,
      placeholder,
      disabled,
      allowCountryChange = true,
      className,
    },
    ref
  ) => {
    const options = getCallingCodeOptions();
    // Several countries share a calling code (e.g. +1), so remember the pick
    const [iso, setIso] = useState("IN");
    const [open, setOpen] = useState(false);

    const selected = findSelectedOption(options, value, iso);
    const nationalNumber = value.slice(selected.callingCode.length);

    return (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        <div
          className={cn(
            "flex items-center h-9 bg-background border border-input rounded-lg focus-within:ring-2 focus-within:ring-ring",
            className
          )}
        >
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
              type="button"
              disabled={disabled || !allowCountryChange}
              className="flex h-full items-center gap-1.5 px-2 border-r border-input rounded-l-lg text-sm font-medium disabled:cursor-default"
            >
              <Flag iso={selected.iso} />
              {selected.callingCode}
              {allowCountryChange && (
                <ChevronDownIcon className="size-4 opacity-50" />
              )}
            </PopoverTrigger>
            {/* Not portalled: the dialog's scroll lock blocks wheel events outside it */}
            <PopoverContent align="start" portal={false} className="w-72 p-2">
              <Command>
                <CommandInput
                  placeholder="Search country"
                  wrapperClassName="flex-row-reverse border border-input rounded-lg [&>svg]:mr-0 [&>svg]:ml-2"
                  className="h-9"
                />
                <CommandList className="max-h-64 mt-2">
                  <CommandEmpty>No country found.</CommandEmpty>
                  <CommandGroup className="p-0">
                    {options.map((option) => (
                      <CommandItem
                        key={option.iso}
                        value={`${option.name} ${option.callingCode}`}
                        onSelect={() => {
                          setIso(option.iso);
                          onChange(option.callingCode + nationalNumber);
                          setOpen(false);
                        }}
                        className={cn(
                          "flex items-center gap-3",
                          option.iso === selected.iso && "bg-accent"
                        )}
                      >
                        <Flag iso={option.iso} />
                        <span className="flex-1 truncate">{option.name}</span>
                        <span className="text-muted-foreground">
                          {option.callingCode}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FormControl>
            <Input
              ref={ref}
              type="tel"
              inputMode="numeric"
              disabled={disabled}
              placeholder={placeholder}
              value={nationalNumber}
              onBlur={onBlur}
              onChange={(e) =>
                onChange(
                  selected.callingCode + e.target.value.replace(/\D/g, "")
                )
              }
              className="flex-1 pl-2 py-0 h-full !ring-0 !outline-none rounded-none rounded-r-lg border-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </FormControl>
        </div>
        <FormMessage />
      </FormItem>
    );
  }
);

FormCountryCodePhoneInput.displayName = "FormCountryCodePhoneInput";
